import { TransactionResponse } from '@ethersproject/providers'
import { createContext, useCallback, useContext, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { AppDispatch, AppState } from '../index'
import { addLimtTransaction, addTransaction, NonBscFarmTransactionType, TransactionType } from './actions'
import { TransactionDetails } from './reducer'
import { Order } from '../../views/LimitOrder/components/LimitOrderTable/limit-orders-lib'

// helper that can take a ethers library transaction response and add it to the list of transactions
export function useTransactionAdder(): (
  response: TransactionResponse,
  customData?: {
    summary?: string
    approval?: { tokenAddress: string; spender: string }
    claim?: { recipient: string }
  },
) => void {
  const { chainId, account } = useActiveWeb3React()
  const dispatch = useDispatch<AppDispatch>()

  return useCallback(
    (
      response: TransactionResponse,
      {
        summary,
        approval,
        claim,
      }: { summary?: string; claim?: { recipient: string }; approval?: { tokenAddress: string; spender: string } } = {},
    ) => {
      if (!account) return
      if (!chainId) return

      const { hash } = response
      if (!hash) {
        throw Error('No transaction hash found.')
      }
      dispatch(addTransaction({ hash, from: account, chainId, approval, summary, claim }))
    },
    [dispatch, chainId, account],
  )
}

// returns all the transactions for the current chain
export function useAllTransactions(): { [txHash: string]: TransactionDetails } {
  const { chainId } = useActiveWeb3React()

  const state = useSelector<AppState, AppState['transactions']>((s) => s.transactions)

  return chainId ? state[chainId] ?? {} : {}
}

export function useIsTransactionPending(transactionHash?: string): boolean {
  const transactions = useAllTransactions()

  if (!transactionHash || !transactions[transactionHash]) return false

  return !transactions[transactionHash].receipt
}

/**
 * Returns whether a transaction happened in the last day (86400 seconds * 1000 milliseconds / second)
 * @param tx to check for recency
 */
export function isTransactionRecent(tx: TransactionDetails): boolean {
  return new Date().getTime() - tx.addedTime < 86_400_000
}

// returns whether a token has a pending approval transaction
export function useHasPendingApproval(tokenAddress: string | undefined, spender: string | undefined): boolean {
  //获取所有交易
  const allTransactions = useAllTransactions()
  return useMemo(
    () =>
      typeof tokenAddress === 'string' &&
      typeof spender === 'string' &&
      Object.keys(allTransactions).some((hash) => {
        const tx = allTransactions[hash]
        if (!tx) return false
        if (tx.receipt) {
          return false
        }
        const { approval } = tx
        if (!approval) return false
        return approval.spender === spender && approval.tokenAddress === tokenAddress && isTransactionRecent(tx)
      }),
    [allTransactions, spender, tokenAddress],
  )
}


export function useLimitTransactionAdder(): (
  response: TransactionResponse,
  customData?: {
    summary?: string
    translatableSummary?: { text: string; data?: Record<string, string | number> }
    approval?: { tokenAddress: string; spender: string }
    claim?: { recipient: string }
    type?: TransactionType
    order?: Order
    nonBscFarm?: NonBscFarmTransactionType
  },
) => void {
  const { chainId, account } = useActiveWeb3React()
  const dispatch = useDispatch<AppDispatch>()

  return useCallback(
    (
      response: TransactionResponse,
      {
        summary,
        translatableSummary,
        approval,
        claim,
        type,
        order,
        nonBscFarm,
      }: {
        summary?: string
        translatableSummary?: { text: string; data?: Record<string, string | number> }
        claim?: { recipient: string }
        approval?: { tokenAddress: string; spender: string }
        type?: TransactionType
        order?: Order
        nonBscFarm?: NonBscFarmTransactionType
      } = {},
    ) => {
      if (!account) return
      if (!chainId) return

      const { hash } = response
      if (!hash) {
        throw Error('No transaction hash found.')
      }
      dispatch(
        addLimtTransaction({
          hash,
          from: account,
          chainId,
          approval,
          summary,
          translatableSummary,
          claim,
          type,
          order,
          nonBscFarm,
        }),
      )
    },
    [dispatch, chainId, account],
  )
}

export function useAllPendingOrders() {
  const allTransactions = useAllTransactions()
  return Object.keys(allTransactions)
    .filter(hash => {
      if (!useIsTransactionPending(hash)){
        return false
      }else if (!allTransactions[hash].order){
        return false
      }else if (allTransactions[hash].type === 'limit-order-submission'){
        return true
      }else{
        return false
      }
    }).map(hash => allTransactions[hash].order)
    // .filter(hash => {
    //   if (allTransactions[hash][RECEIPT]) {
    //     return false
    //   } else if (!allTransactions[hash][RESPONSE]) {
    //     return false
    //   } else if (allTransactions[hash][RESPONSE][CUSTOM_DATA].action === ACTION_PLACE_ORDER) {
    //     return true
    //   } else {
    //     return false
    //   }
    // })
    // .map(hash => allTransactions[hash][RESPONSE][CUSTOM_DATA].order)
}

export function useAllPendingCancelOrders() {
  const allTransactions = useAllTransactions()
  return Object.keys(allTransactions)
    .filter(hash => {
      if (!useIsTransactionPending(hash)) {
        return false
      } else if (!useIsTransactionPending(hash)) {
        return false
      } else if (allTransactions[hash].type === 'limit-order-cancellation') {
        return true
      } else {
        return false
      }
    })
    .map(hash => allTransactions[hash].order)
    // .filter(hash => {
    //   if (allTransactions[hash][RECEIPT]) {
    //     return false
    //   } else if (!allTransactions[hash][RESPONSE]) {
    //     return false
    //   } else if (allTransactions[hash][RESPONSE][CUSTOM_DATA].action === ACTION_CANCEL_ORDER) {
    //     return true
    //   } else {
    //     return false
    //   }
    // })
    // .map(hash => allTransactions[hash][RESPONSE][CUSTOM_DATA].order)
}
